import React, { useState, useMemo } from "react";
import T from '../../../components/common/T';
import { useInternalPicking } from "../../../context/InternalPickingContext";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../components/common/DataTables.jsx";
import TableActionButtons from "../../../components/common/TableActionButtons";
import { TopPrimaryButton } from "../../../components/common/StandardButtons";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../../components/common/PageLayout";
import useModuleFilters from "../../../hooks/useModuleFilters";
import { DocumentTextIcon, CheckCircleIcon, ClockIcon } from "@heroicons/react/24/outline";

export default function InternalPickingList() {
  const { pickings = [] } = useInternalPicking();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [projectFilter, setProjectFilter] = useState("All");

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
  } = useModuleFilters("internal-pickings", {
    projects: [],
    statuses: [],
  });

  // --- STATS DASHBOARD CALCULATION ---
  const stats = useMemo(() => [
    {
      label: "Total Requisitions",
      value: pickings.length,
      icon: DocumentTextIcon,
      variant: "default",
      isActive: statusFilter === "All" || !statusFilter,
      onClick: () => { setStatusFilter("All"); setProjectFilter("All"); },
    },
    {
      label: "Pending",
      value: pickings.filter(p => p.status?.includes("WAITING")).length,
      icon: ClockIcon,
      variant: "orange",
      isActive: statusFilter === "Pending",
      onClick: () => setStatusFilter(s => s === "Pending" ? "All" : "Pending"),
    },
    {
      label: "Approved",
      value: pickings.filter(p => p.status === "APPROVED").length,
      icon: CheckCircleIcon,
      variant: "blue",
      isActive: statusFilter === "Approved",
      onClick: () => setStatusFilter(s => s === "Approved" ? "All" : "Approved"),
    },
    {
      label: "Received",
      value: pickings.filter(p => p.status === "RECEIVED").length,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: statusFilter === "Received",
      onClick: () => setStatusFilter(s => s === "Received" ? "All" : "Received"),
    },
  ], [pickings, statusFilter]);

  const columns = [
    { header: "Reference", accessor: "id", sortable: true, render: (p) => <span className="font-semibold text-purple-700 dark:text-purple-400">{p.id}</span> },
    { header: "Project", accessor: "projectName", sortable: true, render: (p) => <span className="text-gray-800 dark:text-white font-medium">{p.projectName || "-"}</span> },
    { header: "Responsible", accessor: "responsible", sortable: true },
    {
      header: "Status",
      accessor: "status",
      align: "center",
      sortable: true,
      render: (p) => {
        let styles = "bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-700";
        if (p.status === "RECEIVED") styles = "bg-green-50 dark:bg-emerald-950/40 text-green-700 dark:text-emerald-400 border-green-200 dark:border-emerald-800/60";
        if (p.status === "APPROVED") styles = "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/60";
        return <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border ${styles}`}>{p.status}</span>;
      }
    },
    { header: "Date", accessor: "requisitionDate", sortable: true },
    { header: "Actions", align: "right", render: (p) => <TableActionButtons onView={() => navigate(`/admin/material/internal-picking/${p.id}`)} /> }
  ];

  const filteredPickings = useMemo(() => {
    let result = [...pickings];
    if (statusFilter && statusFilter !== "All") {
      if (statusFilter === "Pending") result = result.filter(p => (p.status || '').includes("WAITING"));
      else if (statusFilter === "Approved") result = result.filter(p => p.status === "APPROVED");
      else if (statusFilter === "Received") result = result.filter(p => p.status === "RECEIVED");
      else result = result.filter(p => p.status === statusFilter);
    }
    if (projectFilter && projectFilter !== "All") {
      result = result.filter(p => p.projectName === projectFilter || String(p.projectId) === String(projectFilter));
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(p => 
        p.id?.toString().toLowerCase().includes(s) || 
        p.projectName?.toString().toLowerCase().includes(s) ||
        p.responsible?.toString().toLowerCase().includes(s)
      );
    }
    return result;
  }, [pickings, statusFilter, projectFilter, searchTerm]);

  return (
    <PageLayout>
      <PageHeader
        icon={DocumentTextIcon}
        iconColor="text-[#E8612C]"
        title="Internal Pickings"
        subtitle="Manage internal material movements and requisitions"
        onRefresh={() => window.location.reload()}
      >
        <TopPrimaryButton
          onClick={() => navigate("/admin/material/internal-picking/new")}
          label="Create Picking"
        />
      </PageHeader>
      
      {/* KPI Stats */}
      <StatsGrid stats={stats} cols={4} />

      {/* Unified Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search reference, project, responsible..."
        tabs={['All', 'Pending', 'Approved', 'Received']}
        activeTab={statusFilter}
        onTabChange={setStatusFilter}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      />

      {/* Collapsible Filter Panel */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        onClear={() => { setStatusFilter("All"); setProjectFilter("All"); setSearchTerm(""); }}
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Project</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select
            value={projectFilter}
            onChange={e => setProjectFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Projects</T></option>
            {(filterOptions.projects || []).map((proj) => (
              <option key={proj.id} value={proj.name}>{proj.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1"><T>Status</T></label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Statuses</T></option>
            <option value="Pending"><T>Pending (Waiting)</T></option>
            <option value="Approved"><T>Approved</T></option>
            <option value="Received"><T>Received</T></option>
          </select>
        </div>
      </FilterPanel>

      {/* Table Card */}
      <TableCard>
        <DataTable
          data={filteredPickings}
          columns={columns}
          emptyMessage="No Requisitions Found"
          selectable={true}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </TableCard>
    </PageLayout>
  );
}